// @flow
import React from 'react';
import ReviewAndSubmitVoucherView from './ReviewAndSubmitVoucherView';
import { useReviewAndSubmitVoucherState } from './hooks';

interface ReviewVoucherDetails {
  units: {
    unitsReceivingSubsidy: number,
    unitsAbated: number,
    unitsVacant: number,
    marketRentTenants: number,
  };
  accounting: {
    regularTenantAssistancePayments: number,
    adjustmentsToRegularPayments: number,
    specialClaims: number,
    repaymentAgreements: number,
    miscellaneousAccounting: number,
    totalVoucherAmount: number,
  };
}

interface Props {
  show: boolean;
  voucher: {
    id: string,
    month: string,
    year: string,
    status: string,
    submissionStatus?: string,
    submissionBlockedBy?: { voucherYearMonth?: string },
  };
  reviewDetails?: ReviewVoucherDetails;
  onStatusChange?: (status: string) => void;
  onRefreshVoucher?: () => void;
  onRefreshVoucherDetails?: () => void;
  isReadOnly?: boolean;
  hasUnsubmittedEligibleEvents?: boolean;
  isVoucherEmpty?: boolean;
  submissionBlockers?: Array<string>;
  organizationId?: string;
  propertyId?: string;
  canSubmit: boolean;
}

export default function ReviewAndSubmitVoucher({
  canSubmit,
  hasUnsubmittedEligibleEvents = false,
  isReadOnly: initialReadOnly = false,
  isVoucherEmpty = false,
  onRefreshVoucher,
  onRefreshVoucherDetails,
  onStatusChange,
  organizationId,
  propertyId,
  reviewDetails,
  submissionBlockers = [],
  show,
  voucher,
}: Props): React$Node {
  const {
    handlePrepareExecution,
    handleSubmitToTracs,
    hasSubmissionBlockers,
    hasValidationBlocker,
    hudTracsIntegration,
    isBlockedByPrior,
    isReadOnly,
    isVoucherDraft,
    isVoucherLocked,
    nonDraftTracsTooltip,
    saveExecutionLoading,
    savedExecutionData,
    submitToTracsDisabledTooltip,
    submitVoucherMutation,
  } = useReviewAndSubmitVoucherState({
    voucher,
    onStatusChange,
    onRefreshVoucher,
    onRefreshVoucherDetails,
    isReadOnly: initialReadOnly,
    hasUnsubmittedEligibleEvents,
    isVoucherEmpty,
    organizationId,
    propertyId,
    submissionBlockers,
  });

  if (!show) {
    return null;
  }

  return (
    <ReviewAndSubmitVoucherView
      canSubmit={canSubmit}
      handlePrepareExecution={handlePrepareExecution}
      handleSubmitToTracs={handleSubmitToTracs}
      hasSubmissionBlockers={hasSubmissionBlockers}
      hasUnsubmittedEligibleEvents={hasUnsubmittedEligibleEvents}
      hasValidationBlocker={hasValidationBlocker}
      hudTracsIntegration={hudTracsIntegration}
      isBlockedByPrior={isBlockedByPrior}
      isReadOnly={isReadOnly}
      isVoucherDraft={isVoucherDraft}
      isVoucherEmpty={isVoucherEmpty}
      isVoucherLocked={isVoucherLocked}
      nonDraftTracsTooltip={nonDraftTracsTooltip}
      reviewDetails={reviewDetails}
      saveExecutionLoading={saveExecutionLoading}
      savedExecutionData={savedExecutionData}
      submitToTracsDisabledTooltip={submitToTracsDisabledTooltip}
      submitVoucherMutation={submitVoucherMutation}
      voucher={voucher}
    />
  );
}
